import {
  PacingBarInfo,
  RatioGoalConflict,
  isPacingRatioGoalType,
  pacingGoalNameToKey
} from '@blisspointmedia/bpm-types/dist/Pacing';
import { SuperHeader } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const today = formatInTimeZone(new Date(), 'America/Los_Angeles', 'yyyy-MM-dd');

export const pacingSortByOptions: {
  value: string;
  label: string;
}[] = [
  { value: 'Abc', label: 'Abc' },
  { value: 'goalValue', label: 'Goal Value' }
];

export const isTotalBar = (total: boolean, marginLeft: number): boolean =>
  total || marginLeft === 1;

interface PositionOfBudgetLine {
  positionOfBudgetLine: string;
  xValue: number;
}

export const calculatePositionOfBudgetLine = (
  pacingBarData: PacingBarInfo
): PositionOfBudgetLine => {
  const { totalCleared, totalProjected, budget: goal } = pacingBarData;
  let xValue = 1;
  let positionOfBudgetLine = 'totalCleared';
  if (goal < totalCleared) {
    positionOfBudgetLine = 'totalRemainder';
    xValue = goal / totalCleared;
  } else if (totalCleared < goal && Math.abs(totalCleared - goal) < totalProjected) {
    positionOfBudgetLine = 'totalProjected';
    xValue = (goal - totalCleared) / totalProjected;
  }
  return { positionOfBudgetLine, xValue };
};

// A goal amount as the bars print it: '$' unless non-monetary; 2dp for ratio goals, else thousands + 'k'.
export const formatGoalAmount = (value: number, goalName: string): string => {
  const prefix = NON_MONETARY_GOALS.includes(goalName) ? '' : '$';
  return isPacingRatioGoalType(goalName)
    ? `${prefix}${value.toLocaleString('en-us', { maximumFractionDigits: 2 })}`
    : `${prefix}${(value / 1000).toLocaleString('en-us', { maximumFractionDigits: 2 })}k`;
};

// The budget/goal line's label, e.g. '$600k Budget'.
export const formatGoalLabel = (targetValue: number, goalName: string): string =>
  `${formatGoalAmount(targetValue, goalName)} ${goalName === 'Budget' ? 'Budget' : 'Goal'}`;

export const formatPacingBarValue = (
  pacingValue: number,
  valueTag: string,
  goalValue: number,
  isModal: boolean,
  isActual: boolean,
  goalName: string
): string => {
  if (isPacingRatioGoalType(goalName) && valueTag === '') {
    return '';
  }

  if (isModal) {
    return `${formatGoalAmount(pacingValue, goalName)}/${((pacingValue / goalValue) * 100).toFixed(0)}% ${valueTag}`;
  }

  if (!isActual) {
    return `${((pacingValue / goalValue) * 100).toFixed(0)}% ${valueTag}`;
  }

  if (isPacingRatioGoalType(goalName)) {
    return `${formatGoalAmount(pacingValue, goalName)} ${valueTag}`;
  }

  if (pacingValue < 1000) {
    const prefix = NON_MONETARY_GOALS.includes(goalName) ? '' : '$';
    return `${prefix}${pacingValue.toLocaleString('en-us', { maximumFractionDigits: 0 })} ${valueTag}`;
  }

  return `${formatGoalAmount(pacingValue, goalName)} ${valueTag}`;
};

export const scalePacingBarWidth = (
  currentGoal: number,
  maxGoal: number,
  marginLeft: number
): number => {
  let width = 100 - marginLeft * 25;
  const sizingChange = maxGoal ? (currentGoal / maxGoal) * 15 : 15;
  width += sizingChange;
  return Math.min(90, width);
};

export const BAR_DATA_KEYS = [
  // Goal info
  'currentSegment',
  'allGoalsStart',
  'allGoalsEnd',
  'goalsData',
  'goalValue',
  'goalValueData',
  'booked',
  'adjustedBooked',

  // Simple values
  'simpleDeliveryByDate',
  'simpleDeliveryTotal',
  'simpleProjectedCumulativeByDate',
  'simpleProjectedByDate',
  'simpleProjectedFuture',
  'simpleProjectedTotal',
  'simpleRemainderTotal',

  // Adjusted values
  'adjustedDeliveryTotal',
  'adjustedProjectedByDate',
  'adjustedProjectedCumulativeByDate',
  'adjustedProjectedFuture',
  'adjustedProjectedTotal',
  'adjustedRemainderTotal',

  // Bar data (per-date maps for chart)
  'barSimpleActual',
  'barSimpleIdeal',
  'barAdjustedActual',
  'barAdjustedIdeal'
];

/**
 * Does the bars payload carry a goal for `goalKey` anywhere in the tree? Additive goals sit on the
 * root node; a segmented ratio goal anchors on a child node (its total/root is goalless and ships no
 * `booked`), so we recurse the dimension-child nodes rather than only checking the root's `booked`.
 */
export const barsTreeHasGoal = (
  node: Record<string, any> | undefined,
  goalKey: string
): boolean => {
  if (!node) {
    return false;
  }
  if (node.booked && node.booked[goalKey] != null) {
    return true;
  }
  return Object.entries(node).some(
    ([key, value]) =>
      !BAR_DATA_KEYS.includes(key) &&
      value &&
      typeof value === 'object' &&
      barsTreeHasGoal(value as Record<string, any>, goalKey)
  );
};

// A bars-tree child segment node: any object-valued key that isn't a data field (or the
// 'items'/'null' buckets, which the bars never render as segments).
export const isBarsSegment = (key: string, value: unknown): value is Record<string, unknown> =>
  !BAR_DATA_KEYS.includes(key) &&
  key !== 'items' &&
  key !== 'null' &&
  typeof value === 'object' &&
  value !== null;

// One bars FilterBar line: the node's segment value at each active dimension down to its depth.
export type BarsFilterLine = Record<string, string>;

// Every segment node in the bars tree as a FilterBar line (the tree's levels follow
// `dimensions` in order, so depth N is keyed by dimensions[N - 1]).
export const buildBarsFilterLines = (
  node: Record<string, unknown>,
  dimensions: string[],
  parentLine: BarsFilterLine = {},
  depth = 0
): BarsFilterLine[] => {
  const dimension = dimensions[depth];
  if (!dimension) {
    return [];
  }
  return Object.entries(node).flatMap(([key, value]) => {
    if (!isBarsSegment(key, value)) {
      return [];
    }
    const line = { ...parentLine, [dimension]: key };
    return [line, ...buildBarsFilterLines(value, dimensions, line, depth + 1)];
  });
};

// Prunes the bars tree to the segments whose line passes the FilterBar predicate, plus the
// ancestors of any passing segment. Data fields are kept as-is, so an ancestor bar still shows its
// own (unfiltered) totals; only which child segments render changes.
export const filterBarsTree = (
  node: Record<string, unknown>,
  dimensions: string[],
  predicate: (line: BarsFilterLine) => boolean,
  parentLine: BarsFilterLine = {},
  depth = 0
): Record<string, unknown> => {
  const dimension = dimensions[depth];
  if (!dimension) {
    return node;
  }
  return Object.fromEntries(
    Object.entries(node).flatMap(([key, value]) => {
      if (!isBarsSegment(key, value)) {
        return [[key, value]];
      }
      const line = { ...parentLine, [dimension]: key };
      const child = filterBarsTree(value, dimensions, predicate, line, depth + 1);
      const keepsDescendant = Object.entries(child).some(([k, v]) => isBarsSegment(k, v));
      return predicate(line) || keepsDescendant ? [[key, child]] : [];
    })
  );
};

export const NON_MONETARY_GOALS = ['ROAS', 'CTR', 'KPI Volume'];
// Ratio-goal predicate (ROAS/CPX/CPC/CPM/CTR). Re-exported from bpm-types so the FE and lambda share
// one definition (replaces a locally duplicated GOAL_DERIVED_METRICS array).
export { isPacingRatioGoalType };

// Bars-tab legend labels: goal → [actual, projected, remainder].
export const GOAL_VALUE_MAP: Record<string, string[]> = {
  Budget: ['Actual Spend', 'Projected Spend', 'Remainder'],
  CPX: ['Actual', '', 'Difference'],
  ROAS: ['Actual', '', 'Difference'],
  CPC: ['Actual', '', 'Difference'],
  CPM: ['Actual', '', 'Difference'],
  CTR: ['Actual', '', 'Difference'],
  'KPI Volume': ['Actual', 'Projected', 'Difference'],
  Revenue: ['Actual', 'Projected', 'Difference']
};

// On-bar value-tag labels: goal → [actual, projected, remainder]. Leading space is intentional.
export const GOAL_VALUE_MAP_FOR_BAR_LABELS: Record<string, string[]> = {
  Budget: [' Actual Spend', ' Projected Total', 'Remainder'],
  CPX: [' Actual', '', 'Difference'],
  ROAS: [' Actual', '', 'Difference'],
  CPC: [' Actual', '', 'Difference'],
  CPM: [' Actual', '', 'Difference'],
  CTR: [' Actual', '', 'Difference'],
  'KPI Volume': [' Actual KPI Volume', ' Projected KPI Volume', 'Difference'],
  Revenue: [' Actual Revenue', ' Projected Revenue', 'Difference']
};

// Bars data key for a goal type (Budget→Spend, KPI Volume→KpiVolume, ROAS→Roas, …). Re-exported from
// bpm-types so the FE and the lambda's bar keys come from one implementation.
export const goalNameToKey = pacingGoalNameToKey;

/**
 * Stable, order-independent signature of a ratio-conflict set: `metric:goalId,goalId` per group,
 * sorted and joined. The collision modal re-opens only when this signature changes, so a table
 * refetch that yields the same collisions doesn't re-nag the user.
 */
export const ratioConflictSignature = (conflicts: RatioGoalConflict[]): string =>
  conflicts
    .map(
      c =>
        `${c.metric}:${c.goals
          .map(g => g.originalGoalId)
          .sort()
          .join(',')}`
    )
    .sort()
    .join('|');

// Builds the Over Time super-header CSV row: each label at the start of its column block, with
// leading blanks aligning it under the dimension (Date) columns.
export const buildOverTimeSuperHeaderRow = (
  superHeaders: SuperHeader[],
  totalCols: number
): string[] => {
  const spannedCols = superHeaders.reduce((sum, header) => sum + header.span, 0);
  const numDimCols = Math.max(0, totalCols - spannedCols);
  return [
    ...Array(numDimCols).fill(''),
    ...superHeaders.flatMap(header => [
      header.data.text,
      ...Array(Math.max(0, header.span - 1)).fill('')
    ])
  ];
};

// Whole-number percent of the way `asOf` is through the inclusive [start, end] range, clamped to
// [0, 100]: 0 before the range starts, 100 once it has ended. Dates are yyyy-MM-dd (start -> 0,
// end -> 100; a single-day range -> 100).
export const timePeriodProgress = (start: string, end: string, asOf: string = today): number => {
  const total = differenceInCalendarDays(parseISO(end), parseISO(start));
  if (total <= 0) {
    return 100;
  }
  const elapsed = differenceInCalendarDays(parseISO(asOf), parseISO(start));
  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
};
